import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";
import type { Course, CourseFilter, CreateCourseData } from "../courses/types";
import type { Lesson } from "../lessons/types";
import type {
  AddHomeworkData,
  CreateSessionData,
  FinishPreview,
  FinishResult,
  GradingScheme,
  JoinSessionResult,
  LiveScheduleSlot,
  LiveSession,
  ScheduleSlotInput,
  UpdateLiveSettingsData,
  UpdateSessionData,
  UpsertGradingSchemeData,
} from "./types";

export * from "./types";

/**
 * Jonli kurslar API — yozib olingan kurslarning `CoursesApi` sidan alohida sirt,
 * lekin shakli o'sha. LIVE va RECORDED kurslarning oqimlari boshqacha, shuning
 * uchun ular ro'yxat/yaratish/yuborish endpoint'larini bo'lishmaydi.
 */
export class LiveCoursesApi extends BaseApi {
  private readonly baseUrl: string = "/live-courses";

  /** LIVE kurslarning ommaviy/admin katalogi. */
  getAll(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(this.baseUrl, { params: filter });
  }

  /** Joriy o'qituvchining o'z LIVE kurslari. */
  getMy(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(`${this.baseUrl}/my`, { params: filter });
  }

  create(data: CreateCourseData) {
    return this.request.post<Course, ApiResponse<Course>>(this.baseUrl, data);
  }

  /** Qoralama yoki rad etilgan LIVE kursni moderatsiyaga yuboradi. */
  submit(id: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/submit`);
  }

  remove(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }

  // ── Jonli kurs sozlamalari ──
  getSettings(courseId: number) {
    return this.request.get<Course, ApiResponse<Course>>(`${this.baseUrl}/${courseId}/settings`);
  }

  updateSettings(courseId: number, data: UpdateLiveSettingsData) {
    return this.request.patch<Course, ApiResponse<Course>>(
      `${this.baseUrl}/${courseId}/settings`,
      data,
    );
  }

  // ── Haftalik jadval ──
  getSchedule(courseId: number) {
    return this.request.get<LiveScheduleSlot[], ApiResponse<LiveScheduleSlot[]>>(
      `${this.baseUrl}/${courseId}/schedule`,
    );
  }

  replaceSchedule(courseId: number, slots: ScheduleSlotInput[]) {
    return this.request.put<LiveScheduleSlot[], ApiResponse<LiveScheduleSlot[]>>(
      `${this.baseUrl}/${courseId}/schedule`,
      { slots },
    );
  }

  // ── Sessiyalar ──
  getSessions(courseId: number) {
    return this.request.get<LiveSession[], ApiResponse<LiveSession[]>>(
      `${this.baseUrl}/${courseId}/sessions`,
    );
  }

  generateSessions(courseId: number, replaceExisting = false) {
    return this.request.post<LiveSession[], ApiResponse<LiveSession[]>>(
      `${this.baseUrl}/${courseId}/sessions/generate`,
      { replaceExisting },
    );
  }

  createSession(courseId: number, data: CreateSessionData) {
    return this.request.post<LiveSession, ApiResponse<LiveSession>>(
      `${this.baseUrl}/${courseId}/sessions`,
      data,
    );
  }

  reorderSessions(courseId: number, sessionIds: number[]) {
    return this.request.post<LiveSession[], ApiResponse<LiveSession[]>>(
      `${this.baseUrl}/${courseId}/sessions/reorder`,
      { sessionIds },
    );
  }

  updateSession(sessionId: number, data: UpdateSessionData) {
    return this.request.patch<LiveSession, ApiResponse<LiveSession>>(
      `${this.baseUrl}/sessions/${sessionId}`,
      data,
    );
  }

  removeSession(sessionId: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/sessions/${sessionId}`);
  }

  // ── Darsni olib borish ──
  // Jadval bo'yicha sessiyalarni rejalashtiruvchining o'zi surib boradi; bular
  // esa o'qituvchiga erta boshlash, kech tugatish yoki darsni bekor qilish
  // imkonini beradi.

  /** Xonani hoziroq ochadi va yozilgan o'quvchilarga xabar beradi. */
  startSession(sessionId: number) {
    return this.request.post<LiveSession, ApiResponse<LiveSession>>(
      `${this.baseUrl}/sessions/${sessionId}/start`,
    );
  }

  /** Darsni yakunlaydi: davomatni muzlatadi (kelmaganlar to'ldiriladi), baholashni ochadi. */
  endSession(sessionId: number) {
    return this.request.post<LiveSession, ApiResponse<LiveSession>>(
      `${this.baseUrl}/sessions/${sessionId}/end`,
    );
  }

  cancelSession(sessionId: number, reason?: string) {
    return this.request.post<LiveSession, ApiResponse<LiveSession>>(
      `${this.baseUrl}/sessions/${sessionId}/cancel`,
      { reason },
    );
  }

  /**
   * Sessiyaning uchrashuv havolasi. O'quvchi uchun bu so'rovning o'zi uning
   * kelganini ham qayd etadi — xonani ochish davomatga belgi qo'yish demakdir.
   */
  joinSession(sessionId: number) {
    return this.request.get<JoinSessionResult, ApiResponse<JoinSessionResult>>(
      `${this.baseUrl}/sessions/${sessionId}/join`,
      { skipErrorToast: true },
    );
  }

  // ── Uy vazifasi (har sessiya uchun) ──
  getSessionHomework(sessionId: number) {
    return this.request.get<Lesson[], ApiResponse<Lesson[]>>(
      `${this.baseUrl}/sessions/${sessionId}/homework`,
    );
  }

  addHomework(sessionId: number, data: AddHomeworkData) {
    return this.request.post<Lesson, ApiResponse<Lesson>>(
      `${this.baseUrl}/sessions/${sessionId}/homework`,
      data,
    );
  }

  // ── Baholash sxemasi ──
  getGradingScheme(courseId: number) {
    return this.request.get<GradingScheme | null, ApiResponse<GradingScheme | null>>(
      `${this.baseUrl}/${courseId}/grading-scheme`,
      { skipErrorToast: true },
    );
  }

  upsertGradingScheme(courseId: number, data: UpsertGradingSchemeData) {
    return this.request.put<GradingScheme, ApiResponse<GradingScheme>>(
      `${this.baseUrl}/${courseId}/grading-scheme`,
      data,
    );
  }

  // ── Kurs hayoti: yakunlash va qayta ochish ──

  /** Yakunlashdan oldin: nechta dars bekor bo'ladi, nechta talabaga baho. */
  getFinishPreview(courseId: number) {
    return this.request.get<FinishPreview, ApiResponse<FinishPreview>>(
      `${this.baseUrl}/${courseId}/finish/preview`,
    );
  }

  /** Kursni yakunlaydi (muallif yoki admin). */
  finish(courseId: number) {
    return this.request.post<FinishResult, ApiResponse<FinishResult>>(
      `${this.baseUrl}/${courseId}/finish`,
    );
  }

  /** Yakunlangan kursni qayta ochadi — faqat admin. */
  reopen(courseId: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${courseId}/reopen`);
  }
}

export const liveCoursesApi = new LiveCoursesApi();
